import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableCellProps,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { Permission } from '@yard/app/models/permission.enum'
import { useYardStores } from '@yard/AppProvider'
import { ImoClass, ImoClasses } from '@yard/components/types/ImoClasses'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'

interface StyledHeaderCellProps extends TableCellProps {
  isHighlighted?: boolean
}

interface StyledBodyCellProps extends TableCellProps {
  isSelected?: boolean
  canWrite?: boolean
  isProhibited?: boolean
}

const StyledHeaderCell = styled(({ isHighlighted, ...rest }: StyledHeaderCellProps) => (
  <TableCell {...rest} />
))(({ theme, isHighlighted }) => ({
  backgroundColor: isHighlighted ? theme.palette.info.lighter : theme.palette.grey[200],
  color: isHighlighted ? theme.palette.text.primary : theme.palette.text.secondary,
  fontWeight: 'bold',
  textAlign: 'center',
  padding: 0,
  minWidth: '80px',
  userSelect: 'none',
  width: '4px',
}))

const StyledBodyCell = styled(
  ({ canWrite, isSelected, isProhibited, ...rest }: StyledBodyCellProps) => <TableCell {...rest} />,
)(({ theme, isSelected, canWrite, isProhibited }) => ({
  cursor: canWrite ? 'pointer' : 'default',
  backgroundColor: isSelected ? theme.palette.action.selected : theme.palette.background.paper,
  color: isProhibited ? theme.palette.error.main : 'inherit',
  userSelect: 'none',
  fontWeight: 'bold',
  textAlign: 'center',
  padding: '10px',
  '&:hover': {
    backgroundColor: canWrite ? theme.palette.action.hover : 'inherit',
  },
  width: '40px',
}))

const DGSegregationRulesTable = observer(() => {
  const { dgSegregationRulesTableUIStore, authStore } = useYardStores()
  const [hoveredCell, setHoveredCell] = useState<
    { rowClass: ImoClass; colClass: ImoClass } | undefined
  >()

  useEffect(() => {
    dgSegregationRulesTableUIStore.loadAllDGSegregationRules()
  }, [dgSegregationRulesTableUIStore])

  const canWrite = authStore.hasPermission(Permission.WriteDangerousGoodsSettings)

  const handleMouseDown = (rowClass: ImoClass, colClass: ImoClass) => {
    canWrite && dgSegregationRulesTableUIStore.handleMouseDown(rowClass, colClass)
  }

  const handleMouseEnter = (rowClass: ImoClass, colClass: ImoClass) => {
    setHoveredCell({ rowClass, colClass })
    canWrite && dgSegregationRulesTableUIStore.handleMouseEnter(rowClass, colClass)
  }

  const handleMouseLeave = () => setHoveredCell(undefined)

  const handleMouseUp = () => canWrite && dgSegregationRulesTableUIStore.handleMouseUp()

  return (
    <Box>
      <TableContainer component={Paper} onMouseLeave={handleMouseLeave}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledHeaderCell>IMO classes</StyledHeaderCell>
              {ImoClasses.map(cls => (
                <StyledHeaderCell key={cls} isHighlighted={hoveredCell?.colClass === cls}>
                  {cls}
                </StyledHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {ImoClasses.map(rowClass => (
              <TableRow key={rowClass}>
                <StyledHeaderCell isHighlighted={hoveredCell?.rowClass === rowClass}>
                  {rowClass}
                </StyledHeaderCell>
                {ImoClasses.map(colClass => {
                  const rule = dgSegregationRulesTableUIStore.getRule(rowClass, colClass)

                  return (
                    <StyledBodyCell
                      key={colClass}
                      onMouseDown={() => handleMouseDown(rowClass, colClass)}
                      onMouseEnter={() => handleMouseEnter(rowClass, colClass)}
                      onMouseUp={handleMouseUp}
                      isSelected={dgSegregationRulesTableUIStore.isCellSelected(rowClass, colClass)}
                      canWrite={canWrite}
                      isProhibited={rule?.isProhibited}
                    >
                      {dgSegregationRulesTableUIStore.getRuleDisplayValue(rowClass, colClass)}
                    </StyledBodyCell>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
})

export default DGSegregationRulesTable
